import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import moment from 'moment';

import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { PERMISSION_OBJ_FARMER } from '@/constants/permissions';
import { farmerStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';

import { getFormFields } from './setup';
import { DrawerForm } from '@/components/drawer-form';

const entity = 'farmers';
const store = farmerStore;

export const FarmerForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === `base-layout.${entity}.create`;
  const { farmerId } = transition.router.globals.params; // Correctly use farmerId
  const foundItem = store.items.find(({ id }: any) => id === +farmerId); // Find by farmer ID

  useEffect(() => {
    if (!isCreate && farmerId && !foundItem) {
      api[entity]
        .getOne(+farmerId)
        .source.then(({ data }: any) => store.addItem(data))
        .catch(() => {});
    }
  }, [isCreate, farmerId, foundItem]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const initValues = useMemo(() => {
    if (!isCreate && foundItem) {
      return {
        ...foundItem,
        dob: foundItem.dob ? moment(foundItem.dob) : undefined,
        cooperativeIds: foundItem.cooperatives?.map((c) => c.id) ?? [],
      };
    }
    return {};
  }, [isCreate, foundItem]);

  const title = isCreate
    ? getTranslatedString('farmers.add-new')
    : `${getTranslatedString('farmers.farmerId')}: ${farmerId}`;

  const formFields = getFormFields();
  let allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_FARMER);
  allowedFields = allowedFields.map((field: any) => {
    if (field.name === 'cooperativeIds' && foundItem?.cooperatives) {
      return {
        ...field,
        params: {
          ...field.params,
          initialSelectedOptions: foundItem.cooperatives.map((c) => ({
            value: c.id,
            label: c.name ?? String(c.id),
          })),
        },
      };
    }
    return field;
  });

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => api[entity].create(values).source.then(({ data }: any) => store.addItem(data)),
    update: (values: any) =>
      api[entity].update(foundItem.id, values).source.then(({ data }: any) => store.updateItem(data)),
  });

  return (
    <DrawerForm
      resourceId={farmerId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={!isCreate && !foundItem}
    />
  );
});
